import * as productOrderApi from '@apis/product-orders';
import { RefundStatus } from '@models/paymentStatement';
import { ProductOrderViewModel } from '@models/productOrderView';
import { Purchase, SalesChannelsType } from '@models/sales-channels';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation } from '@tanstack/react-query';
import { formatPhoneNumber } from '@utils/dataTransform';
import { getPaymentMethodText } from '@utils/payment';
import { getPaymentDiscountRows } from '@utils/payment-discount-display';
import { getChannelText } from '@utils/string';
import { Button, Descriptions, Modal, Tag, Typography } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const { Text } = Typography;

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  done: '결제완료',
  error: '결제오류',
  cancel: '결제취소',
  ready: '결제대기',
};

const REFUND_STATUS_LABEL: Record<string, string> = {
  all: '전체환불',
  partial: '부분환불',
  requested: '환불요청',
  accepted: '환불승인',
  denied: '환불반려',
  reserved: '환불예정',
  none: '없음',
};

const KAKAO_STATUS_LABEL: Record<string, string> = {
  REQUESTED: '예약확정대기',
  CONFIRMED: '예약확정',
  COMPLETED: '이용완료',
  CANCELLED: '예약취소',
};

const EZWEL_USE_YN_LABEL: Record<string, string> = {
  Y: '사용처리 완료',
  N: '미사용',
  C: '취소',
};

const EZWEL_SEND_YN_LABEL: Record<string, string> = {
  Y: '발송완료',
  N: '발송실패',
};

const dash = (value?: string | number | null) => {
  if (value === 0) return '0';
  if (value === undefined || value === null || value === '') return '-';
  return `${value}`;
};

const CopyValue: React.FC<{ value?: string | number | null }> = ({ value }) => {
  const text = dash(value);
  if (text === '-') {
    return <>-</>;
  }
  return (
    <Text copyable={{ text }} style={{ margin: 0 }}>
      {text}
    </Text>
  );
};

const formatDateTime = (value?: string | Date | null, format = 'YYYY. MM. DD HH:mm') => {
  if (!value) return '-';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(format) : '-';
};

const formatEzwelDate = (value?: string) => {
  if (!value) return '-';
  if (/^\d{8}$/.test(value)) {
    return moment(value, 'YYYYMMDD').format('YYYY. MM. DD');
  }
  if (/^\d{14}$/.test(value)) {
    return moment(value, 'YYYYMMDDHHmmss').format('YYYY. MM. DD HH:mm:ss');
  }
  return formatDateTime(value);
};

const normalizePurchases = (order?: ProductOrderViewModel | null): Purchase[] => {
  const purchases = order?.purchases as Purchase[] | Purchase | undefined;
  if (Array.isArray(purchases)) {
    return purchases;
  }
  if (purchases) {
    return [purchases];
  }
  if (order?.purchase) {
    return [order.purchase];
  }
  return [];
};

const lastClassDateOf = (order?: ProductOrderViewModel | null) => {
  const dates = (
    order?.remainingPlannedDates?.length
      ? order.remainingPlannedDates
      : order?.plannedDates?.length
        ? order.plannedDates
        : order?.plannedDate
          ? [order.plannedDate]
          : []
  ).filter(Boolean);
  if (!dates.length) {
    return undefined;
  }
  return dates.reduce((latest, current) => {
    if (!latest) return current;
    return new Date(current).getTime() > new Date(latest).getTime() ? current : latest;
  }, dates[0]);
};

const hasClassDatePassed = (order?: ProductOrderViewModel | null) => {
  const lastDate = lastClassDateOf(order);
  if (!lastDate) {
    return false;
  }
  return moment().isSameOrAfter(moment(lastDate));
};

const ezwelOrigin = (order?: ProductOrderViewModel | null) => (order?.origin || {}) as ProductOrderViewModel['origin'];

const patchEzwelUsageComplete = (order?: ProductOrderViewModel | null) => {
  if (!order) {
    return order;
  }
  const now = moment();
  return {
    ...order,
    origin: {
      ...(order.origin || {}),
      useYn: 'Y',
      useCmplDt: now.format('YYYYMMDD'),
      useDtm: now.format('YYYYMMDDHHmmss'),
    },
  };
};

const patchEzwelUsageUncomplete = (order?: ProductOrderViewModel | null) => {
  if (!order) {
    return order;
  }
  return {
    ...order,
    origin: {
      ...(order.origin || {}),
      useYn: 'N',
      useCmplDt: undefined,
      useDtm: undefined,
    },
  };
};

export const canRegisterEzwelUsage = (order?: ProductOrderViewModel | null) => {
  if (!order || order.channel !== SalesChannelsType.EZWEL) {
    return false;
  }
  const origin = ezwelOrigin(order);
  const ecpnTrid = order.ecpnTrid || origin.ecpnTrid;
  if (!ecpnTrid) {
    return false;
  }
  const paymentStatus = `${order.statuses?.payment || ''}`.toLowerCase();
  const refundStatus = `${order.statuses?.refund || ''}`.toLowerCase();
  if (origin.sendSuccYn === 'N' || paymentStatus === 'error') {
    return false;
  }
  if (origin.useYn === 'Y' || origin.useYn === 'C' || paymentStatus === 'cancel' || refundStatus === 'all') {
    return false;
  }
  return true;
};

export const canUnregisterEzwelUsage = (order?: ProductOrderViewModel | null) => {
  if (!order || order.channel !== SalesChannelsType.EZWEL) {
    return false;
  }
  const origin = ezwelOrigin(order);
  const ecpnTrid = order.ecpnTrid || origin.ecpnTrid;
  if (!ecpnTrid) {
    return false;
  }

  const paymentStatus = `${order.statuses?.payment || ''}`.toLowerCase();
  const refundStatus = `${order.statuses?.refund || ''}`.toLowerCase();
  if (origin.sendSuccYn === 'N' || paymentStatus === 'error') {
    return false;
  }

  // 취소(C)면 미사용 처리 대상 아님
  if (origin.useYn !== 'Y') {
    return false;
  }
  if (paymentStatus === 'cancel' || refundStatus === 'all') {
    return false;
  }

  return true;
};

interface ProductOrderDetailModalProps {
  open?: boolean;
  order?: ProductOrderViewModel | null;
  onClose?: () => void;
  onUpdated?: () => void;
}

export const ProductOrderDetailModal: React.FC<ProductOrderDetailModalProps> = ({
  open,
  order,
  onClose,
  onUpdated,
}) => {
  const dialog = useOkCancelDialog();
  const [displayOrder, setDisplayOrder] = useState(order);
  const [ezwelInquiryOpen, setEzwelInquiryOpen] = useState(false);
  const [ezwelInquiryJson, setEzwelInquiryJson] = useState('');

  useEffect(() => {
    if (open) {
      setDisplayOrder(order);
    }
  }, [open, order?.uid, order?.uuid]);

  const origin = ezwelOrigin(displayOrder);
  const purchases = normalizePurchases(displayOrder);
  const lastClassDate = lastClassDateOf(displayOrder);
  const discountRows = getPaymentDiscountRows({
    product: displayOrder?.product,
    payment: displayOrder?.payment,
    purchases: displayOrder?.purchases,
  });
  const isEzwel = displayOrder?.channel === SalesChannelsType.EZWEL;
  const canUsageComplete = canRegisterEzwelUsage(displayOrder);
  const canUsageUncomplete = canUnregisterEzwelUsage(displayOrder);

  const usageCompleteMutation = useMutation({
    mutationFn: (uid: string) => productOrderApi.registerEzwelUsageComplete(uid),
    onSuccess: () => {
      toast.success('이지웰 쿠폰 사용처리가 완료되었습니다.');
      setDisplayOrder((prev) => patchEzwelUsageComplete(prev) ?? prev);
      onUpdated?.();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || '사용처리에 실패했습니다.');
    },
  });

  const usageUncompleteMutation = useMutation({
    mutationFn: (uid: string) => productOrderApi.registerEzwelUsageUncomplete(uid),
    onSuccess: () => {
      toast.success('이지웰 쿠폰 미사용 처리가 완료되었습니다.');
      setDisplayOrder((prev) => patchEzwelUsageUncomplete(prev) ?? prev);
      onUpdated?.();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || '미사용 처리에 실패했습니다.');
    },
  });

  const handleUsageComplete = () => {
    const uid = displayOrder?.uid;
    const couponNo = displayOrder?.ecpnRn || origin.ecpnRn || displayOrder?.ecpnTrid || origin.ecpnTrid;
    if (!uid || !couponNo) return;
    dialog.open({
      type: 'ok_cancel',
      title: '이지웰 쿠폰 사용처리',
      content: hasClassDatePassed(displayOrder)
        ? `쿠폰번호(${couponNo})만 이지웰에 사용 처리합니다. 같은 주문의 다른 쿠폰은 변경되지 않습니다.`
        : `수업일이 지나기 전입니다. 쿠폰번호(${couponNo})만 이지웰에 사용 처리합니다. 같은 주문의 다른 쿠폰은 변경되지 않습니다.`,
      confirmText: '사용처리',
      onConfirm: () => usageCompleteMutation.mutate(uid),
    });
  };

  const ezwelInquiryMutation = useMutation({
    mutationFn: () => {
      const ordNo = `${displayOrder?.ezwelOrdNo || origin.ordNo || displayOrder?.orderId || ''}`.trim();
      const paidAt = displayOrder?.payment?.paidAt;
      const paidDay = paidAt && moment(paidAt).isValid() ? moment(paidAt).format('YYYYMMDD') : undefined;
      return productOrderApi.inquireEzwelOrder({
        ...(ordNo ? { ordNo } : {}),
        ...(paidDay ? { startDate: paidDay, endDate: paidDay } : {}),
      });
    },
    onSuccess: (res) => {
      setEzwelInquiryJson(JSON.stringify(res?.data ?? res, null, 2));
      setEzwelInquiryOpen(true);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || '이지웰 주문조회에 실패했습니다.');
    },
  });

  const handleUsageUncomplete = () => {
    const uid = displayOrder?.uid;
    const couponNo = displayOrder?.ecpnRn || origin.ecpnRn || displayOrder?.ecpnTrid || origin.ecpnTrid;
    if (!uid || !couponNo) return;
    dialog.open({
      type: 'ok_cancel',
      title: '이지웰 쿠폰 미사용 처리',
      content: `쿠폰번호(${couponNo})만 이지웰에 미사용 처리합니다. 같은 주문의 다른 쿠폰은 변경되지 않습니다.`,
      confirmText: '미사용 처리',
      onConfirm: () => usageUncompleteMutation.mutate(uid),
    });
  };

  const renderChannelDetail = () => {
    if (!displayOrder) return null;
    if (displayOrder.channel === SalesChannelsType.EZWEL) {
      return (
        <Descriptions title="이지웰 상세" bordered size="small" column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label="주문번호">
            <CopyValue value={displayOrder.ezwelOrdNo || origin.ordNo || displayOrder.orderId} />
          </Descriptions.Item>
          <Descriptions.Item label="전문번호">
            <CopyValue value={displayOrder.ecpnTrid || origin.ecpnTrid} />
          </Descriptions.Item>
          <Descriptions.Item label="쿠폰번호">
            <CopyValue value={displayOrder.ecpnRn || origin.ecpnRn} />
          </Descriptions.Item>
          <Descriptions.Item label="제휴사 상품코드">
            <CopyValue value={origin.cspGdsCd} />
          </Descriptions.Item>
          <Descriptions.Item label="이지웰 상품코드">
            <CopyValue value={origin.gdsCd} />
          </Descriptions.Item>
          <Descriptions.Item label="쿠폰 발송">
            {EZWEL_SEND_YN_LABEL[origin.sendSuccYn || ''] || dash(origin.sendSuccYn)}
          </Descriptions.Item>
          <Descriptions.Item label="사용처리">
            <Tag color={origin.useYn === 'Y' ? 'green' : origin.useYn === 'C' ? 'red' : 'orange'}>
              {EZWEL_USE_YN_LABEL[origin.useYn || 'N'] || '미사용'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="이용완료일">{formatEzwelDate(origin.useCmplDt)}</Descriptions.Item>
          <Descriptions.Item label="사용일시">{formatEzwelDate(origin.useDtm)}</Descriptions.Item>
          {origin.cnclDtm && <Descriptions.Item label="취소일시">{formatEzwelDate(origin.cnclDtm)}</Descriptions.Item>}
        </Descriptions>
      );
    }

    if (displayOrder.channel === SalesChannelsType.NAVER_STORE) {
      return (
        <Descriptions title="네이버 상세" bordered size="small" column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label="네이버 주문번호">
            <CopyValue value={displayOrder.orderId || origin.orderId || origin.orderNumber} />
          </Descriptions.Item>
          <Descriptions.Item label="상품주문번호">
            <CopyValue value={displayOrder.uid || origin.productOrderId} />
          </Descriptions.Item>
          <Descriptions.Item label="채널상품번호">
            <CopyValue value={displayOrder.product?.channelProductNo || origin.productNumber || origin.productId} />
          </Descriptions.Item>
          <Descriptions.Item label="네이버ID">
            <CopyValue value={displayOrder.buyer?.nId || displayOrder.buyer?.nid} />
          </Descriptions.Item>
          <Descriptions.Item label="결제수단">
            {dash(displayOrder.payment?.method || origin.paymentMethod)}
          </Descriptions.Item>
          <Descriptions.Item label="판매자부담 할인">
            {displayOrder.payment?.sellerBurdenDiscountAmount != null
              ? `${displayOrder.payment.sellerBurdenDiscountAmount.toLocaleString()}원`
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="네이버부담 할인">
            {displayOrder.payment?.naverBurdenDiscountAmount != null
              ? `${displayOrder.payment.naverBurdenDiscountAmount.toLocaleString()}원`
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="정산액">
            {displayOrder.settlement?.estimatedAmount != null
              ? `${displayOrder.settlement.estimatedAmount.toLocaleString()}원`
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="기존 계산 정산액">
            {displayOrder.settlement?.legacyEstimatedAmount != null
              ? `${displayOrder.settlement.legacyEstimatedAmount.toLocaleString()}원`
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="쿠폰 근거">
            {(() => {
              const coupons = displayOrder.payment?.naverDiscountBreakdown || [];
              if (coupons.length === 0) {
                return '-';
              }
              return coupons.map((coupon, index) => (
                <div key={`${coupon.couponPublishNumber || 'coupon'}-${index}`}>
                  {`${coupon.couponPublishNumber || '-'} ${coupon.couponDiscountAmount.toLocaleString()}원 (네이버 ${
                    coupon.naverBurdenRatio
                  }%${coupon.absorbedByIgogo ? ', 아이고고 흡수' : ''})`}
                </div>
              ));
            })()}
          </Descriptions.Item>
          <Descriptions.Item label="주문상태">{dash(origin.orderStatus || origin.orderDetailStatus)}</Descriptions.Item>
        </Descriptions>
      );
    }

    if (displayOrder.channel === SalesChannelsType.KAKAO_RESERVATION) {
      const firstPurchase = purchases[0];
      return (
        <Descriptions title="카카오 상세" bordered size="small" column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label="예약번호">
            <CopyValue value={displayOrder.uid} />
          </Descriptions.Item>
          <Descriptions.Item label="예약상품번호">
            <CopyValue value={firstPurchase?.uid} />
          </Descriptions.Item>
          <Descriptions.Item label="예약상태">
            {KAKAO_STATUS_LABEL[firstPurchase?.status || ''] || dash(firstPurchase?.status)}
          </Descriptions.Item>
          <Descriptions.Item label="결제일시">{formatDateTime(displayOrder.payment?.paidAt)}</Descriptions.Item>
        </Descriptions>
      );
    }

    if (displayOrder.channel === SalesChannelsType.BENEPIA) {
      const pointResult = displayOrder.payment?.benepiaPointResult;
      return (
        <Descriptions title="베네피아 상세" bordered size="small" column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label="주문번호">
            <CopyValue value={displayOrder.uid} />
          </Descriptions.Item>
          <Descriptions.Item label="복지몰 ID">
            <CopyValue value={displayOrder.buyer?.benefit_id} />
          </Descriptions.Item>
          <Descriptions.Item label="베네피아 회원번호">
            <CopyValue value={displayOrder.buyer?.userId} />
          </Descriptions.Item>
          <Descriptions.Item label="사이트코드">
            <CopyValue value={displayOrder.buyer?.sitecode} />
          </Descriptions.Item>
          <Descriptions.Item label="사이트명">{dash(displayOrder.buyer?.sitename)}</Descriptions.Item>
          <Descriptions.Item label="포인트">
            {displayOrder.payment?.point != null ? `${displayOrder.payment.point.toLocaleString()}원` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="청구금액">
            {displayOrder.payment?.billingAmount != null
              ? `${displayOrder.payment.billingAmount.toLocaleString()}원`
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="포인트 승인번호">
            <CopyValue value={pointResult?.pnt_app_no} />
          </Descriptions.Item>
          <Descriptions.Item label="거래번호">
            <CopyValue value={pointResult?.tno} />
          </Descriptions.Item>
        </Descriptions>
      );
    }

    return (
      <Descriptions title="앱 결제 상세" bordered size="small" column={1} style={{ marginTop: 16 }}>
        <Descriptions.Item label="결제 UUID">
          <CopyValue value={displayOrder.uuid || displayOrder.uid} />
        </Descriptions.Item>
        <Descriptions.Item label="게이트웨이">{dash(displayOrder.payment?.gateway)}</Descriptions.Item>
        <Descriptions.Item label="결제수단">{getPaymentMethodText(displayOrder.payment)}</Descriptions.Item>
        <Descriptions.Item label="토스 주문번호">
          <CopyValue value={displayOrder.payment?.result?.data?.orderId || displayOrder.payment?.result?.orderId} />
        </Descriptions.Item>
        <Descriptions.Item label="결제키">
          <CopyValue
            value={displayOrder.payment?.result?.data?.paymentKey || displayOrder.payment?.result?.paymentKey}
          />
        </Descriptions.Item>
        <Descriptions.Item label="포인트">
          {displayOrder.payment?.point != null ? `${displayOrder.payment.point.toLocaleString()}원` : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="쿠폰 할인">
          {displayOrder.payment?.couponDiscountAmount != null
            ? `${displayOrder.payment.couponDiscountAmount.toLocaleString()}원`
            : '-'}
        </Descriptions.Item>
      </Descriptions>
    );
  };

  return (
    <Modal
      title="결제 내역 상세"
      open={!!open && !!displayOrder}
      onCancel={onClose}
      width={720}
      footer={[
        isEzwel && canUsageComplete ? (
          <Button key="usage" type="primary" loading={usageCompleteMutation.isPending} onClick={handleUsageComplete}>
            사용처리
          </Button>
        ) : null,
        isEzwel && canUsageUncomplete ? (
          <Button key="unusage" loading={usageUncompleteMutation.isPending} onClick={handleUsageUncomplete}>
            미사용 처리
          </Button>
        ) : null,
        isEzwel ? (
          <Button key="inquiry" loading={ezwelInquiryMutation.isPending} onClick={() => ezwelInquiryMutation.mutate()}>
            이지웰 주문조회
          </Button>
        ) : null,
        <Button key="close" onClick={onClose}>
          닫기
        </Button>,
      ]}>
      {displayOrder && (
        <>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="채널">
              {getChannelText(displayOrder.channel as SalesChannelsType)}
            </Descriptions.Item>
            <Descriptions.Item label="주문번호">
              <CopyValue value={displayOrder.uid || displayOrder.uuid} />
            </Descriptions.Item>
            <Descriptions.Item label="상품명">{dash(displayOrder.product?.name)}</Descriptions.Item>
            <Descriptions.Item label="옵션">
              {purchases.length
                ? purchases.map((item, index) => (
                    <div key={`${item.name}-${index}`}>
                      {item.name}
                      {item.count ? ` (${item.count})` : ''}
                    </div>
                  ))
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="주문자">
              {`${displayOrder.buyer?.name || '-'} (${formatPhoneNumber(displayOrder.buyer?.phone || '')})`}
            </Descriptions.Item>
            <Descriptions.Item label="결제상태">
              {PAYMENT_STATUS_LABEL[`${displayOrder.statuses?.payment || ''}`.toLowerCase()] ||
                dash(displayOrder.statuses?.payment)}
            </Descriptions.Item>
            <Descriptions.Item label="환불상태">
              {REFUND_STATUS_LABEL[`${displayOrder.statuses?.refund || ''}`.toLowerCase()] ||
                dash(displayOrder.statuses?.refund)}
            </Descriptions.Item>
            <Descriptions.Item label="결제금액">
              {displayOrder.payment?.amount != null ? `${displayOrder.payment.amount.toLocaleString()}원` : '-'}
            </Descriptions.Item>
            {discountRows.length > 0
              ? discountRows.map((row) => (
                  <Descriptions.Item key={row.label} label={row.label}>
                    {row.value}
                  </Descriptions.Item>
                ))
              : null}
            <Descriptions.Item label="결제일시">{formatDateTime(displayOrder.payment?.paidAt)}</Descriptions.Item>
            <Descriptions.Item label="수업일">
              {lastClassDate ? formatDateTime(lastClassDate, 'YYYY. MM. DD HH:mm') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="결제완성일">
              {displayOrder.completionDate ? formatDateTime(displayOrder.completionDate, 'YYYY. MM. DD') : '-'}
            </Descriptions.Item>
          </Descriptions>
          {renderChannelDetail()}
          {isEzwel && origin.useYn !== 'Y' && origin.useYn !== 'C' && !canUsageComplete && (
            <div style={{ marginTop: 12, color: '#888' }}>
              {!displayOrder.ecpnTrid && !origin.ecpnTrid
                ? '전문번호(ecpnTrid)가 없어 쿠폰 단위 사용처리할 수 없습니다.'
                : origin.sendSuccYn === 'N' || `${displayOrder.statuses?.payment || ''}`.toLowerCase() === 'error'
                  ? '발송되지 않은 주문은 사용처리할 수 없습니다.'
                  : `${displayOrder.statuses?.payment || ''}`.toLowerCase() === 'cancel' ||
                      `${displayOrder.statuses?.refund || ''}`.toLowerCase() === RefundStatus.ALL
                    ? '취소/환불된 주문은 사용처리할 수 없습니다.'
                    : '사용처리할 수 없는 주문입니다.'}
            </div>
          )}
        </>
      )}
      <Modal
        title="이지웰 주문내역 조회 (3.2.22)"
        open={ezwelInquiryOpen}
        onCancel={() => setEzwelInquiryOpen(false)}
        width={800}
        footer={[
          <Button
            key="copy"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(ezwelInquiryJson);
                toast.success('JSON을 복사했습니다.');
              } catch {
                toast.error('복사에 실패했습니다.');
              }
            }}>
            복사
          </Button>,
          <Button key="close-json" onClick={() => setEzwelInquiryOpen(false)}>
            닫기
          </Button>,
        ]}>
        <pre
          style={{
            margin: 0,
            maxHeight: '60vh',
            overflow: 'auto',
            background: '#111',
            color: '#eee',
            padding: 12,
            fontSize: 12,
            borderRadius: 6,
          }}>
          {ezwelInquiryJson}
        </pre>
      </Modal>
    </Modal>
  );
};

export default ProductOrderDetailModal;
